import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ticket, Wallet } from 'lucide-react';
import { api } from '../api/cliente.js';
import { useSessao } from '../sessao.js';

/**
 * Seletor do fluxo de automação.
 *
 * Fica na tela de Conexão porque é ali que a pessoa acabou de ligar o número —
 * a pergunta natural em seguida é "e o que ele vai fazer com as mensagens?".
 *
 * A troca é imediata e não pede confirmação: nada é destruído, e voltar é um
 * clique. O que existe é o aviso de consequência, porque a mudança altera como
 * TODA mensagem seguinte é interpretada.
 */

const OPCOES = [
  {
    valor: 'financeiro' as const,
    titulo: 'Financeiro',
    descricao: 'Cada mensagem vira lançamento de entrada ou saída',
    icone: Wallet,
  },
  {
    valor: 'chamados' as const,
    titulo: 'Chamados',
    descricao: 'Cada mensagem abre um chamado no portal de serviços',
    icone: Ticket,
  },
];

export function SeletorAutomacao() {
  const cliente = useQueryClient();
  const { ehAdmin } = useSessao();

  const { data } = useQuery({
    queryKey: ['automacao'],
    queryFn: () => api.automacao(),
  });

  const trocar = useMutation({
    mutationFn: (modo: 'financeiro' | 'chamados') => api.definirModoAutomacao(modo),
    onSuccess: async () => {
      await cliente.invalidateQueries({ queryKey: ['automacao'] });
    },
  });

  const modoAtual = data?.modo ?? 'financeiro';
  const semCredencial = modoAtual === 'chamados' && !data?.credencial;
  const semModelo = modoAtual === 'chamados' && (data?.modelos.length ?? 0) === 0;

  return (
    <section className="mb-4 rounded-[var(--radius-card)] border border-borda bg-superficie p-5 shadow-[var(--shadow-card)]">
      <h2 className="font-semibold text-texto">O que fazer com as mensagens</h2>
      <p className="mt-1 text-[length:var(--text-secundario)] text-texto-secundario">
        Define como o sistema interpreta tudo que você mandar na conversa consigo mesmo.
      </p>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {OPCOES.map((opcao) => {
          const ativo = modoAtual === opcao.valor;
          const Icone = opcao.icone;

          return (
            <button
              key={opcao.valor}
              type="button"
              disabled={!ehAdmin || trocar.isPending}
              onClick={() => !ativo && trocar.mutate(opcao.valor)}
              aria-pressed={ativo}
              className={`flex items-start gap-3 rounded-[var(--radius-card)] border p-4 text-left transition-colors ${
                ativo
                  ? 'border-primaria bg-primaria-bg'
                  : 'border-borda-input bg-superficie hover:border-primaria'
              } ${!ehAdmin ? 'cursor-not-allowed opacity-60' : ''}`}
            >
              <span
                className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-controle)] ${
                  ativo ? 'bg-primaria text-white' : 'bg-fundo text-texto-terciario'
                }`}
              >
                <Icone size={16} aria-hidden />
              </span>
              <span className="min-w-0">
                <span
                  className={`block font-semibold ${ativo ? 'text-primaria-fg' : 'text-texto'}`}
                >
                  {opcao.titulo}
                  {ativo && (
                    <span className="ml-2 text-[length:var(--text-metadado)] font-normal uppercase tracking-[0.05em]">
                      ativo
                    </span>
                  )}
                </span>
                <span className="mt-0.5 block text-[length:var(--text-secundario)] text-texto-secundario">
                  {opcao.descricao}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      {!ehAdmin && (
        <p className="mt-3 text-[length:var(--text-metadado)] text-texto-terciario">
          Apenas administradores podem trocar o fluxo.
        </p>
      )}

      {/* Modo chamados sem configuração é um sistema que aceita a mensagem e
          não faz nada. Melhor dizer o que falta do que deixar descobrir na
          primeira tentativa frustrada. */}
      {(semCredencial || semModelo) && (
        <div className="mt-3 rounded-[var(--radius-controle)] bg-pendente-bg px-3 py-2 text-[length:var(--text-secundario)] text-pendente-fg-sutil">
          <strong>Falta configurar:</strong>{' '}
          {[
            semCredencial ? 'a credencial do portal' : null,
            semModelo ? 'ao menos um modelo de chamado' : null,
          ]
            .filter(Boolean)
            .join(' e ')}
          . Sem isso, os pedidos não serão executados.
        </div>
      )}

      {trocar.isError && (
        <p className="mt-3 rounded-[var(--radius-controle)] bg-saida-bg px-3 py-2 text-[length:var(--text-secundario)] text-saida-fg">
          {trocar.error instanceof Error
            ? trocar.error.message
            : 'Não foi possível trocar o fluxo.'}
        </p>
      )}
    </section>
  );
}
